import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Building2,
  School,
  Users,
  UserPlus,
  Shield,
  Trophy,
  TrendingUp,
  BarChart3,
  Search,
  Upload,
  Download
} from 'lucide-react';
import {
  collection,
  doc,
  addDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { leitnerService } from '../../services/leitnerService';
import { useAuth } from '../../contexts/AuthContext';

const USERS_COLLECTION = 'pb-users';
const LEARNERS_COLLECTION = 'pb-learners';

const ROLE_GROUPS = {
  officials: ['official', 'district_official', 'district-official'],
  educators: ['educator', 'teacher'],
  tutors: ['tutor'],
  learners: ['learner', 'student']
};

const ORG_TYPES = [
  { value: 'district', label: 'District', icon: Building2 },
  { value: 'school', label: 'School', icon: School },
  { value: 'project', label: 'Project', icon: Shield }
];

const GENDER_OPTIONS = ['Female', 'Male'];
const GRADE_OPTIONS = ['8', '9', '10', '11', '12'];
const SUBJECT_OPTIONS = [
  'Mathematics',
  'Physical Sciences',
  'Natural Sciences',
  'Life Sciences',
  'Technical Mathematics',
  'Technical Sciences',
  'Mathematical Literacy',
  'Information Technology'
];
const PEER_PROGRAMME_OPTIONS = ['Yes', 'No'];
const LEARNER_CSV_HEADERS = [
  'names',
  'surname',
  'gender',
  'mobileNumber',
  'email',
  'schoolName',
  'grade',
  'subjects',
  'project',
  'educatorEmails',
  'tutorEmails',
  'parentName',
  'parentMobile',
  'parentEmail',
  'peerTutoringProgramme',
  'plgGroup',
  'plgRole'
];
const LEARNER_CSV_TEMPLATE_ROWS = [
  LEARNER_CSV_HEADERS.join(','),
  'Jane,Doe,Female,+27111222333,jane.doe@example.com,Example High School,11,"Mathematics|Physical Sciences",Math Support,john.educator@example.com,amy.tutor@example.com,Parent Doe,+27821234567,parent.doe@example.com,No,,',
  'Sipho,Ngcobo,Male,+27761234567,sipho.ngcobo@example.com,Example High School,10,Mathematics,Peer Maths Club,john.educator@example.com,,Parent Ngcobo,+27760001111,parent.ngcobo@example.com,Yes,Group A,Peer Leader'
];
const LEARNER_CSV_TEMPLATE = `${LEARNER_CSV_TEMPLATE_ROWS.join('\n')}\n`;

const getUserRole = (user) => user?.affiliation?.role || user?.role || 'user';
const getDisplayName = (user) => user?.displayName || user?.email || user?.id || 'Unknown';

const isRoleIn = (role, list) => list.includes((role || '').toLowerCase());

const getAffiliationValue = (user, key, fallback = '') =>
  user?.affiliation?.[key] || user?.[key] || fallback;

const buildOrganisationsFromAffiliations = (usersList = []) => {
  const orgMap = new Map();

  const addOrg = (type, name, extras = {}) => {
    if (!name) return;
    const id = `${type}:${name}`;
    if (orgMap.has(id)) return;
    orgMap.set(id, { id, type, name, ...extras });
  };

  usersList.forEach((user) => {
    const affiliation = user?.affiliation || {};
    const district = affiliation.district;
    const school = affiliation.school;
    const projectName = affiliation.type === 'project' ? affiliation.name : null;

    addOrg('district', district);
    addOrg('school', school, { district: district || null });
    addOrg('project', projectName, { district: district || null });
  });

  return Array.from(orgMap.values());
};

const LeitnerEntity = ({ bookId }) => {
  const { user, userProfile } = useAuth();
  const [activeSection, setActiveSection] = useState('organisations');
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [learners, setLearners] = useState([]);
  const [learnerRecords, setLearnerRecords] = useState([]);
  const [isSavingLearner, setIsSavingLearner] = useState(false);
  const [learnerError, setLearnerError] = useState(null);
  const [syncStatus, setSyncStatus] = useState('');
  const [syncUpdating, setSyncUpdating] = useState(false);
  const [updateExistingOnSync, setUpdateExistingOnSync] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userSearch, setUserSearch] = useState('');
  const [activeUserTab, setActiveUserTab] = useState('educators');
  const [showLearnerForm, setShowLearnerForm] = useState(false);
  const [editingLearnerId, setEditingLearnerId] = useState(null);
  const [isImportingCsv, setIsImportingCsv] = useState(false);
  const [csvImportStatus, setCsvImportStatus] = useState('');
  const [learnerForm, setLearnerForm] = useState({
    names: '',
    surname: '',
    gender: '',
    mobileNumber: '',
    email: '',
    schoolName: '',
    grade: '',
    subjects: [],
    educatorIds: [],
    project: '',
    tutorIds: [],
    parentName: '',
    parentMobile: '',
    parentEmail: '',
    peerTutoringProgramme: 'No',
    plgGroup: '',
    plgRole: ''
  });
  const csvInputRef = useRef(null);

  const userRole = getUserRole(userProfile);
  const isDistrictOfficial = isRoleIn(userRole, ROLE_GROUPS.officials);
  const canManageLearners = isRoleIn(userRole, [...ROLE_GROUPS.educators, ...ROLE_GROUPS.tutors]);

  useEffect(() => {
    if (!bookId) return;
    loadAll();
  }, [bookId]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [loadedUsers, loadedLearners] = await Promise.all([
        loadUsers(),
        loadLearnerRecords()
      ]);
      const learnersFromUsers = loadedUsers
        .filter((u) => isRoleIn(getUserRole(u), ROLE_GROUPS.learners))
        .map((u) => {
          return {
            ...u,
            userId: u.id,
            managerIds: u.managerIds || [],
            affiliation: u.affiliation || {}
          };
        });
      setUsers(loadedUsers);
      setLearners(learnersFromUsers);
      setLearnerRecords(loadedLearners);
      setOrgs(buildOrganisationsFromAffiliations(loadedUsers));
    } catch (error) {
      console.error('Failed to load entities:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const usersRef = collection(db, USERS_COLLECTION);
      const q = query(
        usersRef,
        where('affiliation.role', 'in', [
          ...ROLE_GROUPS.officials,
          ...ROLE_GROUPS.educators,
          ...ROLE_GROUPS.tutors,
          ...ROLE_GROUPS.learners
        ])
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
    } catch (error) {
      console.error('Failed to load users:', error);
      return [];
    }
  };

  const loadLearnerRecords = async () => {
    try {
      const learnersRef = collection(db, LEARNERS_COLLECTION);
      const snapshot = await getDocs(learnersRef);
      return snapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
    } catch (error) {
      console.error('Failed to load learner records:', error);
      return [];
    }
  };

  const resetLearnerForm = () => {
    setEditingLearnerId(null);
    setLearnerForm({
      names: '',
      surname: '',
      gender: '',
      mobileNumber: '',
      email: '',
      schoolName: '',
      grade: '',
      subjects: [],
      educatorIds: [],
      project: '',
      tutorIds: [],
      parentName: '',
      parentMobile: '',
      parentEmail: '',
      peerTutoringProgramme: 'No',
      plgGroup: '',
      plgRole: ''
    });
    setShowLearnerForm(false);
  };

  const toggleMultiSelectValue = (values, value) => {
    if (values.includes(value)) return values.filter(v => v !== value);
    return [...values, value];
  };

  const parseCsvLine = (line) => {
    const values = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    values.push(current.trim());
    return values;
  };

  const parseCsvRows = (csvText) => {
    const lines = csvText
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean);
    if (lines.length < 2) {
      throw new Error('CSV must include a header row and at least one data row.');
    }

    const headers = parseCsvLine(lines[0]).map((h) => h.trim());
    const missingHeaders = LEARNER_CSV_HEADERS.filter((header) => !headers.includes(header));
    if (missingHeaders.length > 0) {
      throw new Error(`Missing required headers: ${missingHeaders.join(', ')}`);
    }

    return lines.slice(1).map((line, index) => {
      const cells = parseCsvLine(line);
      const row = {};
      headers.forEach((header, headerIndex) => {
        row[header] = (cells[headerIndex] || '').trim();
      });
      return { row, rowNumber: index + 2 };
    });
  };

  const parsePipeList = (value) => (
    (value || '')
      .split('|')
      .map(v => v.trim())
      .filter(Boolean)
  );

  const normalizeProgrammeFlag = (value) => (
    String(value || '').trim().toLowerCase() === 'yes' ? 'Yes' : 'No'
  );

  const toTitleCaseGender = (value) => {
    const normalized = String(value || '').trim().toLowerCase();
    if (normalized === 'female') return 'Female';
    if (normalized === 'male') return 'Male';
    return '';
  };

  const buildLearnerPayloadFromCsv = (row, educatorIdByEmail, tutorIdByEmail) => {
    const subjects = parsePipeList(row.subjects).filter(subject => SUBJECT_OPTIONS.includes(subject));
    const unresolvedSubjects = parsePipeList(row.subjects).filter(subject => !SUBJECT_OPTIONS.includes(subject));
    const educatorIds = parsePipeList(row.educatorEmails)
      .map(email => educatorIdByEmail.get(email.toLowerCase()))
      .filter(Boolean);
    const tutorIds = parsePipeList(row.tutorEmails)
      .map(email => tutorIdByEmail.get(email.toLowerCase()))
      .filter(Boolean);

    const payload = {
      names: (row.names || '').trim(),
      surname: (row.surname || '').trim(),
      gender: toTitleCaseGender(row.gender),
      mobileNumber: (row.mobileNumber || '').trim(),
      email: (row.email || '').trim().toLowerCase(),
      schoolName: (row.schoolName || '').trim(),
      grade: (row.grade || '').trim(),
      subjects,
      educatorIds,
      project: (row.project || '').trim(),
      tutorIds,
      parentName: (row.parentName || '').trim(),
      parentMobile: (row.parentMobile || '').trim(),
      parentEmail: (row.parentEmail || '').trim(),
      peerTutoringProgramme: normalizeProgrammeFlag(row.peerTutoringProgramme),
      plgGroup: (row.plgGroup || '').trim(),
      plgRole: (row.plgRole || '').trim()
    };

    const errors = [];
    if (!payload.names) errors.push('names is required');
    if (!payload.surname) errors.push('surname is required');
    if (!payload.email) errors.push('email is required');
    if (!payload.grade) errors.push('grade is required');
    if (payload.grade && !GRADE_OPTIONS.includes(payload.grade)) {
      errors.push(`grade must be one of ${GRADE_OPTIONS.join('/')}`);
    }
    if (payload.peerTutoringProgramme === 'Yes' && (!payload.plgGroup || !payload.plgRole)) {
      errors.push('plgGroup and plgRole are required when peerTutoringProgramme is Yes');
    }
    if (unresolvedSubjects.length > 0) {
      errors.push(`unsupported subjects: ${unresolvedSubjects.join('|')}`);
    }

    return { payload, errors };
  };

  const handleDownloadLearnerCsvTemplate = () => {
    const blob = new Blob([LEARNER_CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'learner-import-template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleCsvImportClick = () => {
    csvInputRef.current?.click();
  };

  const handleImportLearnersCsv = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLearnerError(null);
    setCsvImportStatus('');
    setIsImportingCsv(true);

    try {
      const csvText = await file.text();
      const rows = parseCsvRows(csvText);
      const existingByEmail = new Map(
        learnerRecords
          .filter(record => record.email)
          .map(record => [String(record.email).toLowerCase(), record])
      );

      const educatorIdByEmail = new Map(
        educatorsList
          .filter(educator => educator.email)
          .map(educator => [String(educator.email).toLowerCase(), educator.id])
      );
      const tutorIdByEmail = new Map(
        tutorsList
          .filter(tutor => tutor.email)
          .map(tutor => [String(tutor.email).toLowerCase(), tutor.id])
      );

      let createdCount = 0;
      let updatedCount = 0;
      let skippedCount = 0;
      const rowErrors = [];

      for (const { row, rowNumber } of rows) {
        const { payload, errors } = buildLearnerPayloadFromCsv(row, educatorIdByEmail, tutorIdByEmail);
        if (errors.length > 0) {
          skippedCount += 1;
          rowErrors.push(`Row ${rowNumber}: ${errors.join('; ')}`);
          continue;
        }

        const existing = existingByEmail.get(payload.email);
        if (existing) {
          await updateDoc(doc(db, LEARNERS_COLLECTION, existing.id), {
            ...payload,
            updatedAt: serverTimestamp()
          });
          updatedCount += 1;
        } else {
          await addDoc(collection(db, LEARNERS_COLLECTION), {
            ...payload,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          createdCount += 1;
        }
      }

      await loadAll();

      const summary = `CSV import complete. Added ${createdCount}, updated ${updatedCount}, skipped ${skippedCount}.`;
      setCsvImportStatus(summary);
      if (rowErrors.length > 0) {
        setLearnerError(`Some rows were skipped: ${rowErrors.slice(0, 5).join(' | ')}${rowErrors.length > 5 ? ' ...' : ''}`);
      }
    } catch (error) {
      console.error('Failed to import learners CSV:', error);
      setLearnerError(error.message || 'Failed to import CSV.');
    } finally {
      setIsImportingCsv(false);
      if (event.target) event.target.value = '';
    }
  };

  const validateLearnerForm = () => {
    if (!learnerForm.names.trim() || !learnerForm.surname.trim()) {
      return 'Name(s) and Surname are required.';
    }
    if (!learnerForm.email.trim()) {
      return 'Email is required.';
    }
    if (!learnerForm.grade) {
      return 'Grade is required.';
    }
    if (learnerForm.peerTutoringProgramme === 'Yes') {
      if (!learnerForm.plgGroup.trim() || !learnerForm.plgRole.trim()) {
        return 'PLG Group and PLG Role are required when Peer Tutoring Programme is Yes.';
      }
    }
    return null;
  };

  const handleSaveLearner = async () => {
    const validationError = validateLearnerForm();
    if (validationError) {
      setLearnerError(validationError);
      return;
    }

    setLearnerError(null);
    setIsSavingLearner(true);
    try {
      const payload = {
        ...learnerForm,
        names: learnerForm.names.trim(),
        surname: learnerForm.surname.trim(),
        email: learnerForm.email.trim(),
        mobileNumber: learnerForm.mobileNumber.trim(),
        schoolName: learnerForm.schoolName.trim(),
        project: learnerForm.project.trim(),
        parentName: learnerForm.parentName.trim(),
        parentMobile: learnerForm.parentMobile.trim(),
        parentEmail: learnerForm.parentEmail.trim(),
        plgGroup: learnerForm.peerTutoringProgramme === 'Yes' ? learnerForm.plgGroup.trim() : '',
        plgRole: learnerForm.peerTutoringProgramme === 'Yes' ? learnerForm.plgRole.trim() : '',
        updatedAt: serverTimestamp()
      };

      if (editingLearnerId) {
        await updateDoc(doc(db, LEARNERS_COLLECTION, editingLearnerId), payload);
      } else {
        await addDoc(collection(db, LEARNERS_COLLECTION), {
          ...payload,
          createdAt: serverTimestamp()
        });
      }
      await loadAll();
      resetLearnerForm();
    } catch (error) {
      console.error('Failed to save learner:', error);
      setLearnerError('Failed to save learner record.');
    } finally {
      setIsSavingLearner(false);
    }
  };

  const handleEditLearner = (learner) => {
    setEditingLearnerId(learner.id);
    setLearnerForm({
      names: learner.names || '',
      surname: learner.surname || '',
      gender: learner.gender || '',
      mobileNumber: learner.mobileNumber || '',
      email: learner.email || '',
      schoolName: learner.schoolName || '',
      grade: learner.grade || '',
      subjects: learner.subjects || [],
      educatorIds: learner.educatorIds || [],
      project: learner.project || '',
      tutorIds: learner.tutorIds || [],
      parentName: learner.parentName || '',
      parentMobile: learner.parentMobile || '',
      parentEmail: learner.parentEmail || '',
      peerTutoringProgramme: learner.peerTutoringProgramme || 'No',
      plgGroup: learner.plgGroup || '',
      plgRole: learner.plgRole || ''
    });
    setShowLearnerForm(true);
  };

  const mapUserToLearnerPayload = (userEntry) => {
    const displayName = getDisplayName(userEntry);
    const nameParts = displayName.split(' ').filter(Boolean);
    const surname = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
    const names = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : displayName;

    const affiliation = userEntry.affiliation || {};

    return {
      userId: userEntry.id,
      names,
      surname,
      gender: userEntry.gender || '',
      mobileNumber: userEntry.phoneNumber || userEntry.mobile || '',
      email: userEntry.email || '',
      schoolName: affiliation.school || userEntry.school || '',
      grade: affiliation.grade || userEntry.grade || '',
      subjects: affiliation.subjects || userEntry.subjects || [],
      educatorIds: userEntry.educatorIds || [],
      project: affiliation.project || affiliation.name || userEntry.project || '',
      tutorIds: userEntry.tutorIds || [],
      parentName: userEntry.parentName || '',
      parentMobile: userEntry.parentMobile || '',
      parentEmail: userEntry.parentEmail || '',
      peerTutoringProgramme: userEntry.peerTutoringProgramme || 'No',
      plgGroup: userEntry.plgGroup || '',
      plgRole: userEntry.plgRole || ''
    };
  };

  const handleSyncLearners = async () => {
    setSyncUpdating(true);
    setSyncStatus('Syncing learner records...');
    try {
      const learnerUsers = users.filter((u) => isRoleIn(getUserRole(u), ROLE_GROUPS.learners));
      const existingByUserId = new Map(learnerRecords.map((l) => [l.userId, l]));
      const existingByEmail = new Map(learnerRecords.map((l) => [l.email, l]));

      const operations = learnerUsers.map(async (userEntry) => {
        const payload = mapUserToLearnerPayload(userEntry);
        const existing = existingByUserId.get(userEntry.id) || existingByEmail.get(payload.email);
        if (existing) {
          if (!updateExistingOnSync) return null;
          await updateDoc(doc(db, LEARNERS_COLLECTION, existing.id), {
            ...payload,
            updatedAt: serverTimestamp()
          });
          return 'updated';
        }
        await addDoc(collection(db, LEARNERS_COLLECTION), {
          ...payload,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        return 'created';
      });

      const results = await Promise.all(operations);
      const created = results.filter(r => r === 'created').length;
      const updated = results.filter(r => r === 'updated').length;
      setSyncStatus(`Sync complete. Added ${created}, updated ${updated}.`);
      await loadAll();
    } catch (error) {
      console.error('Failed to sync learners:', error);
      setSyncStatus('Sync failed. Check console for details.');
    } finally {
      setSyncUpdating(false);
      setTimeout(() => setSyncStatus(''), 4000);
    }
  };


  const handleAssignLearner = async (learnerId) => {
    if (!user?.uid) return;
    const learner = learners.find(l => l.id === learnerId);
    if (!learner) return;
    const managerIds = Array.from(new Set([...(learner.managerIds || []), user.uid]));
    await updateDoc(doc(db, USERS_COLLECTION, learnerId), {
      managerIds,
      updatedAt: serverTimestamp()
    });
    await loadAll();
  };

  const visibleOrgs = useMemo(() => {
    if (!isDistrictOfficial) return orgs;
    const districtName = getAffiliationValue(userProfile, 'district');
    if (!districtName) return [];
    return orgs.filter(org => {
      if (org.type === 'district') return org.name === districtName;
      if (org.district) return org.district === districtName;
      if (org.parentDistrict) return org.parentDistrict === districtName;
      return false;
    });
  }, [orgs, isDistrictOfficial, userProfile]);

  const learnerStats = useLearnerStats(bookId, users);
  const orgStats = useOrgStats(visibleOrgs, learnerStats);

  const filteredUsers = useMemo(() => {
    const queryText = userSearch.trim().toLowerCase();
    if (!queryText) return users;
    return users.filter(u => {
      const name = getDisplayName(u).toLowerCase();
      const email = (u.email || '').toLowerCase();
      const district = getAffiliationValue(u, 'district').toLowerCase();
      const school = getAffiliationValue(u, 'school').toLowerCase();
      return name.includes(queryText) || email.includes(queryText) || district.includes(queryText) || school.includes(queryText);
    });
  }, [users, userSearch]);

  const filteredUsersByRole = useMemo(() => {
    const buckets = { officials: [], educators: [], tutors: [], learners: [] };
    filteredUsers.forEach((u) => {
      const role = getUserRole(u).toLowerCase();
      if (isRoleIn(role, ROLE_GROUPS.officials)) buckets.officials.push(u);
      else if (isRoleIn(role, ROLE_GROUPS.educators)) buckets.educators.push(u);
      else if (isRoleIn(role, ROLE_GROUPS.tutors)) buckets.tutors.push(u);
      else if (isRoleIn(role, ROLE_GROUPS.learners)) buckets.learners.push(u);
    });
    return buckets;
  }, [filteredUsers]);

  const filteredLearners = useMemo(() => {
    if (filteredUsersByRole.learners.length === 0) return [];
    const learnerIds = new Set(filteredUsersByRole.learners.map((u) => u.id));
    return learners.filter((learner) => learnerIds.has(learner.id));
  }, [filteredUsersByRole, learners]);

  const educatorsList = useMemo(() => (
    users.filter((u) => isRoleIn(getUserRole(u), ROLE_GROUPS.educators))
  ), [users]);

  const tutorsList = useMemo(() => (
    users.filter((u) => isRoleIn(getUserRole(u), ROLE_GROUPS.tutors))
  ), [users]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Leitner Entity Manager</h3>
          <p className="text-sm text-gray-500">
            Manage organisations, educators, and learner records with district-aware permissions.
          </p>
        </div>
        {isDistrictOfficial && (
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full text-xs font-medium">
            <Shield className="h-4 w-4" />
            District official access (view-only)
          </span>
        )}
      </div>

      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <div className="flex border-b">
          <button
            onClick={() => setActiveSection('organisations')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeSection === 'organisations'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Organisations
          </button>
          <button
            onClick={() => setActiveSection('users')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeSection === 'users'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Users & Learners
          </button>
        </div>

        {loading ? (
          <div className="p-10 text-center text-gray-500">Loading entities...</div>
        ) : activeSection === 'organisations' ? (
          <div className="p-6 space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h4 className="font-semibold text-gray-900">Organisation Overview</h4>
                <p className="text-sm text-gray-500">District, school, and project performance summaries.</p>
              </div>
              <div className="text-xs text-gray-500">
                Organisations are derived from user affiliation data.
              </div>
            </div>

            {visibleOrgs.length === 0 ? (
              <div className="text-center py-10 text-gray-500 bg-gray-50 rounded-lg">
                No organisations found yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {visibleOrgs.map(org => {
                  const stats = orgStats[org.id];
                  const OrgIcon = ORG_TYPES.find(type => type.value === org.type)?.icon || Building2;
                  return (
                    <div key={org.id} className="border rounded-xl p-4 bg-white shadow-sm">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="p-2 rounded-lg bg-indigo-50 text-indigo-600">
                              <OrgIcon className="h-4 w-4" />
                            </span>
                            <div>
                              <h5 className="font-semibold text-gray-900">{org.name}</h5>
                              <p className="text-xs text-gray-500 capitalize">{org.type}</p>
                            </div>
                          </div>
                          {org.district && (
                            <p className="text-xs text-gray-500 mt-2">District: {org.district}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">Learners</p>
                          <p className="text-lg font-semibold text-gray-900">{stats?.learnersCount || 0}</p>
                        </div>
                      </div>

                      {stats ? (
                        <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                          <div className="bg-gray-50 rounded-lg p-2">
                            <p className="text-xs text-gray-500">Attempts</p>
                            <p className="text-sm font-semibold text-gray-900">{stats.totalAttempts}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-2">
                            <p className="text-xs text-gray-500">Accuracy</p>
                            <p className="text-sm font-semibold text-emerald-600">{stats.accuracy}%</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-2">
                            <p className="text-xs text-gray-500">Improvement</p>
                            <p className={`text-sm font-semibold ${stats.improvement >= 0 ? 'text-indigo-600' : 'text-red-500'}`}>
                              {stats.improvement >= 0 ? '+' : ''}{stats.improvement}%
                            </p>
                          </div>
                        </div>
                      ) : (
                        <p className="mt-4 text-xs text-gray-500">No learner activity tracked yet.</p>
                      )}

                      {stats && (
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                          <Leaderboard title="Most Engaged" icon={BarChart3} entries={stats.leaderboards.engaged} />
                          <Leaderboard title="Best Performers" icon={Trophy} entries={stats.leaderboards.performers} />
                          <Leaderboard title="Improving Fastest" icon={TrendingUp} entries={stats.leaderboards.improvers} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="p-6 space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h4 className="font-semibold text-gray-900">Educators & Learners</h4>
                <p className="text-sm text-gray-500">Manage learner records and educator teams.</p>
              </div>
              <div className="relative w-full lg:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Search users..."
                  className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2 border-b pb-3">
              {[
                { key: 'learners', label: 'Learners', count: filteredUsersByRole.learners.length },
                { key: 'educators', label: 'Educators', count: filteredUsersByRole.educators.length },
                { key: 'tutors', label: 'Tutors', count: filteredUsersByRole.tutors.length },
                { key: 'officials', label: 'Officials', count: filteredUsersByRole.officials.length }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveUserTab(tab.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border transition ${
                    activeUserTab === tab.key
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:text-gray-900'
                  }`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </div>

            {activeUserTab === 'learners' ? (
              <div className="space-y-6">
                <div className="bg-white rounded-xl border p-4 space-y-4 shadow-sm">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div>
                      <h5 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Users className="h-4 w-4 text-indigo-600" />
                        Learner Records (pb-learners)
                      </h5>
                      <p className="text-xs text-gray-500">
                        Create or update learner profiles stored in pb-learners.
                      </p>
                    </div>
                    <div className="flex flex-col md:flex-row md:items-center gap-2">
                      <label className="flex items-center gap-2 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          checked={updateExistingOnSync}
                          onChange={(e) => setUpdateExistingOnSync(e.target.checked)}
                          className="h-4 w-4"
                        />
                        Update existing on sync
                      </label>
                      <button
                        onClick={handleSyncLearners}
                        disabled={syncUpdating}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50 disabled:opacity-50"
                      >
                        {syncUpdating ? 'Syncing...' : 'Sync Learners from Users'}
                      </button>
                      <button
                        onClick={() => setShowLearnerForm((prev) => !prev)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                      >
                        {showLearnerForm ? 'Hide Form' : 'New Learner'}
                      </button>
                      <button
                        onClick={handleDownloadLearnerCsvTemplate}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
                      >
                        <Download className="h-3.5 w-3.5" />
                        CSV Template
                      </button>
                      <button
                        onClick={handleCsvImportClick}
                        disabled={isImportingCsv}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                      >
                        <Upload className="h-3.5 w-3.5" />
                        {isImportingCsv ? 'Importing CSV...' : 'Import CSV'}
                      </button>
                      <input
                        ref={csvInputRef}
                        type="file"
                        accept=".csv,text/csv"
                        onChange={handleImportLearnersCsv}
                        className="hidden"
                      />
                    </div>
                  </div>

                  {syncStatus && (
                    <div className="text-xs text-gray-600 bg-gray-50 border rounded-lg px-3 py-2">
                      {syncStatus}
                    </div>
                  )}
                  {csvImportStatus && (
                    <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                      {csvImportStatus}
                    </div>
                  )}
                  <div className="text-xs text-gray-500">
                    CSV notes: use `|` to separate multiple values in `subjects`, `educatorEmails`, and `tutorEmails`.
                  </div>

                  {learnerError && (
                    <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      {learnerError}
                    </div>
                  )}

                  {showLearnerForm && (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Name(s)</label>
                          <input
                            type="text"
                            value={learnerForm.names}
                            onChange={(e) => setLearnerForm(prev => ({ ...prev, names: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Surname</label>
                          <input
                            type="text"
                            value={learnerForm.surname}
                            onChange={(e) => setLearnerForm(prev => ({ ...prev, surname: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Gender</label>
                          <select
                            value={learnerForm.gender}
                            onChange={(e) => setLearnerForm(prev => ({ ...prev, gender: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                          >
                            <option value="">Select gender</option>
                            {GENDER_OPTIONS.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Mobile Number</label>
                          <input
                            type="text"
                            value={learnerForm.mobileNumber}
                            onChange={(e) => setLearnerForm(prev => ({ ...prev, mobileNumber: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Email</label>
                          <input
                            type="email"
                            value={learnerForm.email}
                            onChange={(e) => setLearnerForm(prev => ({ ...prev, email: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">School Name</label>
                          <input
                            type="text"
                            value={learnerForm.schoolName}
                            onChange={(e) => setLearnerForm(prev => ({ ...prev, schoolName: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Grade</label>
                          <select
                            value={learnerForm.grade}
                            onChange={(e) => setLearnerForm(prev => ({ ...prev, grade: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                          >
                            <option value="">Select grade</option>
                            {GRADE_OPTIONS.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Project</label>
                          <input
                            type="text"
                            value={learnerForm.project}
                            onChange={(e) => setLearnerForm(prev => ({ ...prev, project: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs text-gray-500 mb-2">Subjects</label>
                        <div className="flex flex-wrap gap-2">
                          {SUBJECT_OPTIONS.map(subject => (
                            <label key={subject} className="flex items-center gap-2 text-xs text-gray-600 border rounded-full px-3 py-1">
                              <input
                                type="checkbox"
                                checked={learnerForm.subjects.includes(subject)}
                                onChange={() => setLearnerForm(prev => ({ ...prev, subjects: toggleMultiSelectValue(prev.subjects, subject) }))}
                                className="h-3.5 w-3.5"
                              />
                              {subject}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-2">Educators</label>
                          <div className="border rounded-lg p-2 max-h-40 overflow-y-auto space-y-1">
                            {educatorsList.length === 0 ? (
                              <p className="text-xs text-gray-400">No educators available.</p>
                            ) : (
                              educatorsList.map(educator => (
                                <label key={educator.id} className="flex items-center gap-2 text-xs text-gray-600">
                                  <input
                                    type="checkbox"
                                    checked={learnerForm.educatorIds.includes(educator.id)}
                                    onChange={() => setLearnerForm(prev => ({
                                      ...prev,
                                      educatorIds: toggleMultiSelectValue(prev.educatorIds, educator.id)
                                    }))}
                                    className="h-3.5 w-3.5"
                                  />
                                  {getDisplayName(educator)}
                                </label>
                              ))
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-2">Tutor(s)</label>
                          <div className="border rounded-lg p-2 max-h-40 overflow-y-auto space-y-1">
                            {tutorsList.length === 0 ? (
                              <p className="text-xs text-gray-400">No tutors available.</p>
                            ) : (
                              tutorsList.map(tutor => (
                                <label key={tutor.id} className="flex items-center gap-2 text-xs text-gray-600">
                                  <input
                                    type="checkbox"
                                    checked={learnerForm.tutorIds.includes(tutor.id)}
                                    onChange={() => setLearnerForm(prev => ({
                                      ...prev,
                                      tutorIds: toggleMultiSelectValue(prev.tutorIds, tutor.id)
                                    }))}
                                    className="h-3.5 w-3.5"
                                  />
                                  {getDisplayName(tutor)}
                                </label>
                              ))
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Parent Name</label>
                          <input
                            type="text"
                            value={learnerForm.parentName}
                            onChange={(e) => setLearnerForm(prev => ({ ...prev, parentName: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Parent Mobile</label>
                          <input
                            type="text"
                            value={learnerForm.parentMobile}
                            onChange={(e) => setLearnerForm(prev => ({ ...prev, parentMobile: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Parent Email</label>
                          <input
                            type="email"
                            value={learnerForm.parentEmail}
                            onChange={(e) => setLearnerForm(prev => ({ ...prev, parentEmail: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Peer Tutoring Programme</label>
                          <select
                            value={learnerForm.peerTutoringProgramme}
                            onChange={(e) => setLearnerForm(prev => ({ ...prev, peerTutoringProgramme: e.target.value }))}
                            className="w-full px-3 py-2 border rounded-lg text-sm"
                          >
                            {PEER_PROGRAMME_OPTIONS.map(option => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {learnerForm.peerTutoringProgramme === 'Yes' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">PLG Group</label>
                            <input
                              type="text"
                              value={learnerForm.plgGroup}
                              onChange={(e) => setLearnerForm(prev => ({ ...prev, plgGroup: e.target.value }))}
                              className="w-full px-3 py-2 border rounded-lg text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">PLG Role</label>
                            <input
                              type="text"
                              value={learnerForm.plgRole}
                              onChange={(e) => setLearnerForm(prev => ({ ...prev, plgRole: e.target.value }))}
                              className="w-full px-3 py-2 border rounded-lg text-sm"
                            />
                          </div>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={handleSaveLearner}
                          disabled={isSavingLearner}
                          className="inline-flex items-center gap-1 px-4 py-2 text-xs rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                        >
                          {editingLearnerId ? 'Update Learner' : 'Save Learner'}
                        </button>
                        <button
                          onClick={resetLearnerForm}
                          type="button"
                          className="inline-flex items-center gap-1 px-4 py-2 text-xs rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                        >
                          Clear Form
                        </button>
                      </div>
                    </>
                  )}

                  <div className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-gray-700">Existing Learner Records</p>
                      <span className="text-xs text-gray-500">{learnerRecords.length} records</span>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {learnerRecords.length === 0 ? (
                        <p className="text-xs text-gray-400">No learner records saved yet.</p>
                      ) : (
                        learnerRecords.map(record => (
                          <div key={record.id} className="flex items-center justify-between gap-3 bg-white border rounded-lg px-3 py-2 text-xs">
                            <div>
                              <p className="font-medium text-gray-800">{record.names} {record.surname}</p>
                              <p className="text-gray-500">{record.email || 'No email'} · Grade {record.grade || 'N/A'}</p>
                            </div>
                            <button
                              onClick={() => handleEditLearner(record)}
                              className="px-2 py-1 text-xs rounded border border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                            >
                              Edit
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl border p-4 space-y-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div>
                    <h5 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Users className="h-4 w-4 text-indigo-600" />
                      Learners
                    </h5>
                    <p className="text-xs text-gray-500">
                      Learner records are loaded from pb-users using the affiliation role.
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  {filteredLearners.length === 0 ? (
                    <div className="text-center text-gray-500 py-6">No learners found.</div>
                  ) : (
                    filteredLearners.map((learner) => {
                      const profile = users.find(u => u.id === learner.userId);
                      const displayName = getDisplayName(profile || learner);
                      const district = getAffiliationValue(profile || learner, 'district', 'Unassigned');
                      const school = getAffiliationValue(profile || learner, 'school', 'Unassigned');
                      const isManaged = (learner.managerIds || []).includes(user?.uid);
                      return (
                        <div key={learner.id} className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-white border rounded-lg p-3">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{displayName}</p>
                            <p className="text-xs text-gray-500">{district} - {school}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {canManageLearners && (
                              <button
                                onClick={() => handleAssignLearner(learner.id)}
                                disabled={isManaged}
                                className="inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50 disabled:opacity-50"
                              >
                                <UserPlus className="h-3.5 w-3.5" />
                                {isManaged ? 'Assigned' : 'Assign to me'}
                              </button>
                            )}
                            {(learner.managerIds || []).length > 0 && (
                              <span className="text-xs text-gray-500">
                                Managers: {learner.managerIds.length}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
            ) : (
              <RoleCard
                title={
                  activeUserTab === 'educators'
                    ? 'Educators'
                    : activeUserTab === 'tutors'
                      ? 'Tutors'
                      : 'District Officials'
                }
                icon={activeUserTab === 'officials' ? Shield : Users}
                users={
                  activeUserTab === 'educators'
                    ? filteredUsersByRole.educators
                    : activeUserTab === 'tutors'
                      ? filteredUsersByRole.tutors
                      : filteredUsersByRole.officials
                }
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const RoleCard = ({ title, icon: Icon, users }) => (
  <div className="bg-white border rounded-xl p-4 shadow-sm">
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <span className="p-2 rounded-lg bg-indigo-50 text-indigo-600">
          <Icon className="h-4 w-4" />
        </span>
        <div>
          <p className="font-semibold text-gray-900">{title}</p>
          <p className="text-xs text-gray-500">{users.length} people</p>
        </div>
      </div>
    </div>
    <div className="space-y-2 max-h-48 overflow-y-auto">
      {users.length === 0 ? (
        <p className="text-xs text-gray-500">No users found.</p>
      ) : (
        users.slice(0, 8).map(user => (
          <div key={user.id} className="text-sm text-gray-700">
            {getDisplayName(user)}
            <span className="text-xs text-gray-400"> · {getAffiliationValue(user, 'district') || 'No district'}</span>
          </div>
        ))
      )}
    </div>
  </div>
);

const Leaderboard = ({ title, icon: Icon, entries }) => (
  <div className="bg-gray-50 rounded-lg p-3">
    <div className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
      <Icon className="h-3.5 w-3.5" />
      {title}
    </div>
    {entries.length === 0 ? (
      <p className="text-xs text-gray-400">No data yet.</p>
    ) : (
      <div className="space-y-1">
        {entries.map((entry) => (
          <div key={entry.userId} className="flex items-center justify-between text-xs text-gray-600">
            <span className="truncate">{entry.name}</span>
            <span className="font-medium text-gray-900">{entry.value}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

const useLearnerStats = (bookId, users) => {
  const [stats, setStats] = useState([]);

  useEffect(() => {
    if (!bookId) return;
    let mounted = true;
    const load = async () => {
      try {
        const progress = await leitnerService.getAllLearnersProgress(bookId);
        const userMap = new Map(users.map(u => [u.id, u]));
        const now = new Date();
        const recentCutoff = new Date(now);
        recentCutoff.setDate(now.getDate() - 7);

        const computed = progress.map((learner) => {
          const profile = userMap.get(learner.userId);
          const items = [...(learner.questions || []), ...(learner.subquestions || [])].map((item) => {
            const lastReviewed = item.lastReviewed?.toDate
              ? item.lastReviewed.toDate()
              : item.lastReviewed
                ? new Date(item.lastReviewed)
                : null;
            return {
              attempts: item.attempts || 0,
              correctAttempts: item.correctAttempts || 0,
              lastReviewed
            };
          });

          const totalAttempts = items.reduce((sum, item) => sum + item.attempts, 0);
          const totalCorrect = items.reduce((sum, item) => sum + item.correctAttempts, 0);
          const accuracy = totalAttempts > 0 ? Math.round((totalCorrect / totalAttempts) * 100) : 0;

          const recentItems = items.filter(item => item.lastReviewed && item.lastReviewed >= recentCutoff);
          const recentAttempts = recentItems.reduce((sum, item) => sum + item.attempts, 0);
          const recentCorrect = recentItems.reduce((sum, item) => sum + item.correctAttempts, 0);
          const recentAccuracy = recentAttempts > 0 ? Math.round((recentCorrect / recentAttempts) * 100) : 0;

          const improvement = recentAccuracy - accuracy;

          return {
            userId: learner.userId,
            name: getDisplayName(profile || {}),
            affiliation: profile?.affiliation || {},
            totalAttempts,
            accuracy,
            improvement
          };
        });

        if (mounted) setStats(computed);
      } catch (error) {
        console.error('Failed to load learner stats:', error);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [bookId, users]);

  return stats;
};

const useOrgStats = (orgs, learnerStats) => {
  return useMemo(() => {
    const byOrgId = {};
    orgs.forEach((org) => {
      const learners = learnerStats.filter((learner) => {
        const district = learner.affiliation?.district;
        const school = learner.affiliation?.school;
        if (org.type === 'district') return district === org.name;
        if (org.type === 'school') return school === org.name || org.school === school;
        if (org.type === 'project') {
          const projectName = learner.affiliation?.project || learner.affiliation?.name;
          return projectName === org.name;
        }
        return false;
      });

      const totalAttempts = learners.reduce((sum, l) => sum + l.totalAttempts, 0);
      const avgAccuracy = learners.length > 0
        ? Math.round(learners.reduce((sum, l) => sum + l.accuracy, 0) / learners.length)
        : 0;
      const avgImprovement = learners.length > 0
        ? Math.round(learners.reduce((sum, l) => sum + l.improvement, 0) / learners.length)
        : 0;

      const leaderboards = {
        engaged: [...learners].sort((a, b) => b.totalAttempts - a.totalAttempts).slice(0, 5).map(l => ({
          userId: l.userId,
          name: l.name,
          value: l.totalAttempts
        })),
        performers: [...learners].sort((a, b) => b.accuracy - a.accuracy).slice(0, 5).map(l => ({
          userId: l.userId,
          name: l.name,
          value: `${l.accuracy}%`
        })),
        improvers: [...learners].sort((a, b) => b.improvement - a.improvement).slice(0, 5).map(l => ({
          userId: l.userId,
          name: l.name,
          value: `${l.improvement >= 0 ? '+' : ''}${l.improvement}%`
        }))
      };

      byOrgId[org.id] = {
        learnersCount: learners.length,
        totalAttempts,
        accuracy: avgAccuracy,
        improvement: avgImprovement,
        leaderboards
      };
    });
    return byOrgId;
  }, [orgs, learnerStats]);
};

export default LeitnerEntity;
